/**
 * System Log Page
 *
 * Main admin page for viewing application logs, API request logs and query performance logs,
 * plus the Retention tab (how long each log table is kept; see RetentionTab).
 * Features: tabbed interface, stats cards, DataTable display, live WebSocket updates via modal.
 * Pattern: Similar to Permissions and Users pages
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  IconFileText,
  IconApi,
  IconDatabase,
  IconRefresh,
  IconAlertCircle,
  IconMessageCircle,
  IconAlertTriangle,
  IconClockCog,
} from '@tabler/icons-react';
import {
  Container,
  Stack,
  Card,
  Group,
  Button,
  Alert,
  Text,
  Loader,
  Grid,
  Tabs,
  Title,
  Select,
} from '../../../components/ui';
import { FadeIn, SlideIn } from '../../../components/animations';
import { ApplicationLogsTab } from './components/ApplicationLogsTab';
import { ApiLogsTab } from './components/ApiLogsTab';
import { QueryLogsTab } from './components/QueryLogsTab';
import { DeadLetterTab } from './components/DeadLetterTab';
import { RetentionTab } from './components/RetentionTab';
import { useSystemLogs } from './hooks/useSystemLogs';
import type { AppLogDto, ApiRequestLogDto, QueryPerformanceLogDto } from '@actschurch/ccms-sdk';
import type { DeadLetterMessage } from './types';

/** Markers that identify a log line as messaging/consumer related. */
const MESSAGING_MESSAGE_PATTERN =
  /\[RECV|\[DONE|\[DISPATCH|\[Consumer|\[ConsumerDiagnostics|BACKLOG|Enqueued message|Published message|RabbitMQ|dead-letter|consumer queue|channel (?:went down|shutdown)/i;

/** Application logs from worker services, or any log about message publishing/consuming. */
function isMessagingLog(log: AppLogDto): boolean {
  const service = (log.serviceName ?? '').toLowerCase();
  if (service.includes('worker')) return true;
  return MESSAGING_MESSAGE_PATTERN.test(log.message ?? '');
}

export default function SystemLogPage() {
  const systemLogs = useSystemLogs();

  // State
  const [appLogs, setAppLogs] = useState<AppLogDto[]>([]);
  const [apiLogs, setApiLogs] = useState<ApiRequestLogDto[]>([]);
  const [queryLogs, setQueryLogs] = useState<QueryPerformanceLogDto[]>([]);
  const [deadLetters, setDeadLetters] = useState<DeadLetterMessage[]>([]);
  const [deadLetterAvailable, setDeadLetterAvailable] = useState(true);
  const [deadLetterError, setDeadLetterError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('application');
  const [logCount, setLogCount] = useState<string>('100');

  /**
   * Fetch all log data
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const count = parseInt(logCount) || 100;
    try {
      // Fetch logs individually to handle partial failures
      const results = await Promise.allSettled([
        systemLogs.getRecentAppLogs(count),
        systemLogs.getRecentApiLogs(count),
        systemLogs.getRecentQueryLogs(count),
        systemLogs.getDeadLetterMessages(50),
      ]);

      // Handle application logs
      if (results[0].status === 'fulfilled') {
        setAppLogs(results[0].value);
      } else {
        console.error('Error fetching application logs:', results[0].reason);
      }

      // Handle API logs
      if (results[1].status === 'fulfilled') {
        setApiLogs(results[1].value);
      } else {
        console.error('Error fetching API logs:', results[1].reason);
      }

      // Handle query logs
      if (results[2].status === 'fulfilled') {
        setQueryLogs(results[2].value);
      } else {
        console.error('Error fetching query logs:', results[2].reason);
        // Query logs are optional, don't show error to user
      }

      // Handle dead-letter messages (optional — surfaced inside its own tab)
      if (results[3].status === 'fulfilled') {
        const dlq = results[3].value;
        setDeadLetters(dlq?.messages ?? []);
        setDeadLetterAvailable(dlq?.available ?? false);
        setDeadLetterError(dlq?.error ?? null);
      } else {
        console.error('Error fetching dead-letter messages:', results[3].reason);
        setDeadLetterAvailable(false);
        setDeadLetterError('Dead-letter endpoint unavailable');
      }

      // Only show a page-level error if all three log requests failed
      const allFailed = results.slice(0, 3).every((r) => r.status === 'rejected');
      if (allFailed) {
        setError('Failed to fetch system logs. Please try again.');
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch system logs';
      setError(errorMessage);
      console.error('Error fetching system logs:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logCount]);

  /**
   * Initial load
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Calculate stats
   */
  const stats = useMemo(() => {
    const totalAppLogs = appLogs.length;
    const totalApiRequests = apiLogs.length;
    const totalQueries = queryLogs.length;
    const recentErrors = appLogs.filter(
      (log) => log.logLevel === 'Error' || log.logLevel === 'Critical'
    ).length;

    return {
      totalAppLogs,
      totalApiRequests,
      totalQueries,
      recentErrors,
    };
  }, [appLogs, apiLogs, queryLogs]);

  /**
   * Messaging logs — the subset of application logs from worker services or
   * about message publishing/consuming.
   */
  const messagingLogs = useMemo(() => appLogs.filter(isMessagingLog), [appLogs]);

  return (
    <Container size="xl">
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Group gap="sm">
                <IconFileText size={32} />
                <Title order={2}>System Logs</Title>
              </Group>
              <Text c="dimmed" size="sm">
                Monitor application logs, API requests and database queries, and choose how long logs are kept
              </Text>
            </Stack>
            <Group gap="sm">
              <Select
                value={logCount}
                onChange={(value) => setLogCount(value || '100')}
                data={[
                  { value: '100', label: '100 logs' },
                  { value: '250', label: '250 logs' },
                  { value: '500', label: '500 logs' },
                  { value: '1000', label: '1000 logs' },
                ]}
                style={{ width: 130 }}
                size="sm"
              />
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchData}
                loading={loading}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* Error Alert */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Error"
              withCloseButton
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Stats Cards */}
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={0}>
                <Card variant="gradient" gradientVariant="primary" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Application Logs
                      </Text>
                      <IconFileText size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.totalAppLogs}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={100}>
                <Card variant="gradient" gradientVariant="secondary" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        API Requests
                      </Text>
                      <IconApi size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.totalApiRequests}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={200}>
                <Card variant="gradient" gradientVariant="info" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Database Queries
                      </Text>
                      <IconDatabase size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.totalQueries}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SlideIn delay={300}>
                <Card variant="gradient" gradientVariant="danger" padding="md">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                        Recent Errors
                      </Text>
                      <IconAlertCircle size={20} color="white" style={{ opacity: 0.8 }} />
                    </Group>
                    <Text size="xl" fw={700} c="white">
                      {stats.recentErrors}
                    </Text>
                  </Stack>
                </Card>
              </SlideIn>
            </Grid.Col>
          </Grid>

          {/* Tabs */}
          <Card>
            <Tabs
              value={activeTab}
              onChange={(value) => setActiveTab(value || 'application')}
              tabsVariant="pills"
            >
              <Tabs.List>
                <Tabs.Tab value="application" leftSection={<IconFileText size={16} />}>
                  Application Logs
                </Tabs.Tab>
                <Tabs.Tab value="messaging" leftSection={<IconMessageCircle size={16} />}>
                  Messaging Logs
                </Tabs.Tab>
                <Tabs.Tab value="api" leftSection={<IconApi size={16} />}>
                  API Logs
                </Tabs.Tab>
                <Tabs.Tab value="query" leftSection={<IconDatabase size={16} />}>
                  Query Logs
                </Tabs.Tab>
                <Tabs.Tab value="dead-letter" leftSection={<IconAlertTriangle size={16} />}>
                  Dead Letter
                </Tabs.Tab>
                <Tabs.Tab value="retention" leftSection={<IconClockCog size={16} />}>
                  Retention
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="application" pt="md">
                {loading && appLogs.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <ApplicationLogsTab
                    logs={appLogs}
                    onRefresh={fetchData}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="messaging" pt="md">
                {loading && messagingLogs.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <ApplicationLogsTab
                    logs={messagingLogs}
                    onRefresh={fetchData}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="api" pt="md">
                {loading && apiLogs.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <ApiLogsTab
                    logs={apiLogs}
                    onRefresh={fetchData}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="query" pt="md">
                {loading && queryLogs.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <QueryLogsTab
                    logs={queryLogs}
                    onRefresh={fetchData}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="dead-letter" pt="md">
                {loading && deadLetterAvailable && deadLetters.length === 0 ? (
                  <Group justify="center" p="xl">
                    <Loader size="lg" />
                  </Group>
                ) : (
                  <DeadLetterTab
                    messages={deadLetters}
                    available={deadLetterAvailable}
                    loadError={deadLetterError}
                    onRefresh={fetchData}
                    loading={loading}
                  />
                )}
              </Tabs.Panel>

              <Tabs.Panel value="retention" pt="md">
                {activeTab === 'retention' && <RetentionTab />}
              </Tabs.Panel>
            </Tabs>
          </Card>
        </Stack>
      </FadeIn>
    </Container>
  );
}
